import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, MapPin, Quote, Check, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import StickyFooterNav from '../components/store/StickyFooterNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── FALLBACK DATA ────────────────────────────────────────────────────────────
// Used only on first load before /api/portfolio responds. Admin can replace via
// the Portfolio admin page. Empty arrays cause the corresponding section to
// gracefully unmount (see `Section` wrapper below).
const FALLBACK = {
  brand: { name: '', tagline: '' },
  hero: { headline: '', subhead: '', image: '' },
  whatsapp_number: '',
  projects: [],
  services: [],
  about: { image: '', headline: '', story: '', stats: [] },
  testimonials: [],
  pricing: [],
};

// Section wrapper — graceful empty-state. If `show` is false the section unmounts.
const Section = ({ id, show, children, className = '' }) => {
  if (show === false) return null;
  return (
    <section id={id} className={`py-20 md:py-32 ${className}`}>{children}</section>
  );
};

const Container = ({ children, className = '' }) => (
  <div className={`max-w-7xl mx-auto px-5 md:px-10 ${className}`}>{children}</div>
);

const SerifH2 = ({ children, className = '' }) => (
  <h2 className={`font-serif font-light tracking-tight leading-[1.05] text-stone-900 text-4xl md:text-6xl ${className}`} style={{ fontFamily: '"Playfair Display", serif' }}>{children}</h2>
);

// Convert YouTube / Vimeo URLs into embeddable player URLs.
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace('www.', '');
    // YouTube
    if (host === 'youtu.be') {
      const id = u.pathname.replace('/', '').split('/')[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname === '/watch') return u.searchParams.get('v') ? `https://www.youtube.com/embed/${u.searchParams.get('v')}` : null;
      if (u.pathname.startsWith('/embed/')) return url;
      if (u.pathname.startsWith('/shorts/')) return `https://www.youtube.com/embed/${u.pathname.split('/')[2]}`;
    }
    // Vimeo
    if (host === 'vimeo.com') {
      const id = u.pathname.replace('/', '').split('/')[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    if (host === 'player.vimeo.com') return url;
  } catch (_) { /* fall through */ }
  return null;
}

// Lightbox modal: full-screen carousel of images + optional embedded video.
const Lightbox = ({ project, onClose }) => {
  const images = (project?.images || []).filter(Boolean);
  const videoEmbed = toEmbedUrl(project?.video_url);
  const slides = [...images.map(src => ({ type: 'image', src })), ...(videoEmbed ? [{ type: 'video', src: videoEmbed }] : [])];
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [project]);
  useEffect(() => {
    if (!project) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => (i + 1) % slides.length);
      if (e.key === 'ArrowLeft') setIdx(i => (i - 1 + slides.length) % slides.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [project, slides.length, onClose]);

  if (!project || slides.length === 0) return null;
  const cur = slides[idx];

  return (
    <div data-testid="portfolio-lightbox" className="fixed inset-0 z-[60] bg-stone-900/95 flex items-center justify-center p-4 md:p-8" onClick={onClose}>
      <button data-testid="lightbox-close" onClick={onClose} className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white p-2" aria-label="Close">
        <X className="w-6 h-6" />
      </button>
      <div className="relative max-w-6xl w-full h-full flex flex-col items-center justify-center gap-4" onClick={e => e.stopPropagation()}>
        <div className="w-full flex-1 min-h-0 flex items-center justify-center">
          {cur.type === 'image' ? (
            <img src={cur.src} alt={project.title} className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="w-full aspect-video max-h-full max-w-5xl">
              <iframe src={cur.src} title="Project video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </div>
        <div className="text-white/80 text-sm text-center px-4">
          <div className="font-serif text-xl md:text-2xl text-white mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>{project.title}</div>
          {project.location && <div className="text-white/60 text-xs uppercase tracking-widest flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />{project.location}</div>}
          {slides.length > 1 && <div className="text-white/50 text-xs mt-3">{idx + 1} / {slides.length}</div>}
        </div>
        {slides.length > 1 && (
          <>
            <button data-testid="lightbox-prev" onClick={() => setIdx(i => (i - 1 + slides.length) % slides.length)} className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full backdrop-blur" aria-label="Previous">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="lightbox-next" onClick={() => setIdx(i => (i + 1) % slides.length)} className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full backdrop-blur" aria-label="Next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function Portfolio() {
  const [d, setD] = useState(FALLBACK);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openProject, setOpenProject] = useState(null);
  const waLink = d.whatsapp_number ? `https://wa.me/${d.whatsapp_number}?text=Hi%20${encodeURIComponent(d.brand?.name || '')}%2C%20I%20saw%20your%20portfolio%20and%20would%20like%20to%20chat.` : null;

  useEffect(() => {
    let mounted = true;
    document.body.style.fontFamily = '"Outfit", system-ui, sans-serif';
    axios.get(`${BACKEND_URL}/api/portfolio`).then(res => {
      if (mounted && res.data) setD({ ...FALLBACK, ...res.data });
    }).catch(() => { /* keep fallback */ });
    return () => { mounted = false; document.body.style.fontFamily = ''; };
  }, []);

  return (
    <div data-testid="portfolio-page" className="min-h-screen bg-stone-50 text-stone-900" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
      <StickyFooterNav whatsappNumber={d.whatsapp_number} />
      {/* ── Sticky header ──────────────────────────────────────────────── */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-stone-50/75 border-b border-stone-200/70">
        <Container className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            <span data-testid="portfolio-brand" className="font-serif text-2xl tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>{d.brand?.name || 'Studio'}</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600">
            <Link to="/" data-testid="portfolio-nav-shop" className="hover:text-stone-900 transition-colors">Shop</Link>
            {d.projects?.length > 0 && <a href="#projects" className="hover:text-stone-900 transition-colors">Projects</a>}
            {d.services?.length > 0 && <a href="#services" className="hover:text-stone-900 transition-colors">Services</a>}
            {d.about && <a href="#about" className="hover:text-stone-900 transition-colors">About</a>}
            {d.testimonials?.length > 0 && <a href="#voices" className="hover:text-stone-900 transition-colors">Voices</a>}
            {d.pricing?.length > 0 && <a href="#pricing" className="hover:text-stone-900 transition-colors">Pricing</a>}
          </nav>
          <Link to="/get-quote" data-testid="header-get-quote-btn" className="hidden md:inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-5 py-2.5 rounded-none transition-colors">
            Get a Quote <ArrowRight className="w-4 h-4" />
          </Link>
          <button onClick={() => setMenuOpen(v => !v)} className="md:hidden p-2" data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </Container>
        {menuOpen && (
          <div className="md:hidden border-t border-stone-200 bg-stone-50 px-5 py-4 flex flex-col gap-3 text-sm">
            <Link to="/" onClick={() => setMenuOpen(false)} data-testid="portfolio-nav-shop-mobile">Shop</Link>
            {d.projects?.length > 0 && <a href="#projects" onClick={() => setMenuOpen(false)}>Projects</a>}
            {d.services?.length > 0 && <a href="#services" onClick={() => setMenuOpen(false)}>Services</a>}
            {d.about && <a href="#about" onClick={() => setMenuOpen(false)}>About</a>}
            {d.testimonials?.length > 0 && <a href="#voices" onClick={() => setMenuOpen(false)}>Voices</a>}
            {d.pricing?.length > 0 && <a href="#pricing" onClick={() => setMenuOpen(false)}>Pricing</a>}
            <Link to="/get-quote" className="bg-amber-700 text-white px-4 py-2 inline-flex items-center justify-center gap-1 mt-2">Get a Quote <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      {(d.hero?.headline || d.hero?.image || d.hero?.subhead) && (
      <section data-testid="hero-section" className="pt-24 md:pt-28 pb-12 md:pb-16 min-h-[calc(100vh-72px)] flex items-center">
        <Container className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center w-full">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-5">{d.brand?.tagline || ''}</p>
            <h1 className="font-serif font-light tracking-tighter leading-[0.95] text-stone-900 text-4xl md:text-[3.75rem] lg:text-[4.5rem] mb-6 whitespace-pre-line" style={{ fontFamily: '"Playfair Display", serif' }}>
              {d.hero.headline}
            </h1>
            <p className="text-stone-600 text-base md:text-lg leading-relaxed mb-8 max-w-md">{d.hero.subhead}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/get-quote" data-testid="hero-get-quote-btn" className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-base font-medium px-7 py-3.5 transition-colors">
                Get a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              {waLink && (
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-900 text-stone-900 text-base font-medium px-7 py-3.5 transition-colors">
                  <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
                </a>
              )}
            </div>
          </div>
          <div className="md:col-span-7">
            {d.hero.image && (
              <div className="aspect-[4/5] md:aspect-auto md:h-[calc(100vh-160px)] md:max-h-[640px] bg-stone-100 flex items-center justify-center overflow-hidden">
                <img src={d.hero.image} alt="Hero" className="max-w-full max-h-full w-auto h-auto object-contain" />
              </div>
            )}
          </div>
        </Container>
      </section>
      )}

      {/* ── Completed Projects ─────────────────────────────────────────── */}
      <Section id="projects" show={d.projects?.length > 0}>
        <Container>
          <div className="flex items-end justify-between mb-12 md:mb-16 gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">Selected work</p>
              <SerifH2>Completed projects.</SerifH2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[260px]">
            {d.projects.map((p, idx) => {
              const cover = (p.images && p.images[0]) || p.image || '';
              const imgCount = (p.images && p.images.length) || (p.image ? 1 : 0);
              const hasVideo = !!toEmbedUrl(p.video_url);
              const total = imgCount + (hasVideo ? 1 : 0);
              const hasMedia = total > 0;
              return (
              <button
                key={idx}
                type="button"
                data-testid={`project-card-${idx}`}
                onClick={() => hasMedia && setOpenProject(p)}
                className={`group relative overflow-hidden bg-stone-200 text-left ${p.span || ''} ${hasMedia ? 'cursor-zoom-in' : 'cursor-default'}`}
              >
                {cover && <img src={cover} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/85 via-stone-900/0 to-transparent opacity-90 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500" />
                {total > 1 && (
                  <span className="absolute top-3 right-3 z-10 bg-black/60 backdrop-blur text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full opacity-100">
                    {hasVideo && imgCount > 0 ? `${imgCount} + video` : (hasVideo ? 'video' : `${imgCount} photos`)}
                  </span>
                )}
                <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 text-white translate-y-2 md:translate-y-6 md:group-hover:translate-y-0 transition-transform duration-500">
                  <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>{p.title}</h3>
                  <p className="text-xs uppercase tracking-widest text-white/70 flex items-center gap-1.5 mb-2"><MapPin className="w-3 h-3" />{p.location}</p>
                  {p.description && <p className="text-sm text-white/85 max-w-md leading-relaxed">{p.description}</p>}
                </div>
              </button>
            );})}
          </div>
        </Container>
      </Section>

      {/* ── Services ───────────────────────────────────────────────────── */}
      <Section id="services" show={d.services?.length > 0} className="border-t border-stone-200">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
            <div className="md:col-span-4">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">What we do</p>
              <SerifH2>Services.</SerifH2>
            </div>
            <div className="md:col-span-8 md:pt-2">
              <ul>
                {d.services.map((s, idx) => (
                  <li key={idx} data-testid={`service-${idx}`} className="group border-t border-stone-200 py-7 md:py-8 cursor-default">
                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3">
                      <h3 className="font-serif text-2xl md:text-4xl text-stone-900 leading-tight transition-colors group-hover:text-amber-800" style={{ fontFamily: '"Playfair Display", serif' }}>{s.title}</h3>
                      <p className="text-stone-600 text-sm md:text-base max-w-sm md:text-right leading-relaxed">{s.blurb}</p>
                    </div>
                  </li>
                ))}
                <li className="border-t border-stone-200" />
              </ul>
            </div>
          </div>
        </Container>
      </Section>

      {/* ── About / Story ──────────────────────────────────────────────── */}
      <Section id="about" show={Boolean(d.about?.headline || d.about?.story || d.about?.image)} className="border-t border-stone-200">
        <Container className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5">
            {d.about?.image && (
              <div className="aspect-[3/4] md:aspect-auto md:h-[520px] bg-stone-100 flex items-center justify-center overflow-hidden">
                <img src={d.about.image} alt="The studio" className="max-w-full max-h-full w-auto h-auto object-contain" />
              </div>
            )}
          </div>
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">The studio</p>
            <SerifH2 className="mb-8">{d.about?.headline || 'About us'}</SerifH2>
            {d.about?.story && (
              <p className="text-stone-700 text-lg leading-relaxed first-letter:font-serif first-letter:text-7xl first-letter:font-light first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-amber-800" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>
                {d.about.story}
              </p>
            )}
            {d.about?.stats?.length > 0 && (
              <div className="grid grid-cols-3 gap-6 mt-12 pt-10 border-t border-stone-200">
                {d.about.stats.map((s, idx) => (
                  <div key={idx}>
                    <p className="font-serif text-3xl md:text-4xl text-stone-900 leading-none mb-1" style={{ fontFamily: '"Playfair Display", serif' }}>{s.value}</p>
                    <p className="text-xs uppercase tracking-widest text-stone-500">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* ── Testimonials ───────────────────────────────────────────────── */}
      <Section id="voices" show={d.testimonials?.length > 0} className="border-t border-stone-200">
        <Container>
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3 text-center">Voices</p>
          <SerifH2 className="text-center mb-16 md:mb-20">What clients say.</SerifH2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {d.testimonials.map((t, idx) => (
              <figure key={idx} data-testid={`testimonial-${idx}`} className="relative">
                <Quote className="w-7 h-7 text-amber-700/40 mb-4" />
                <blockquote className="font-serif text-xl md:text-2xl leading-snug text-stone-800 mb-6" style={{ fontFamily: '"Playfair Display", serif', fontStyle: 'italic' }}>
                  {t.quote}
                </blockquote>
                <figcaption className="text-sm">
                  <p className="font-semibold text-stone-900">{t.author}</p>
                  {t.context && <p className="text-stone-500 text-xs uppercase tracking-widest mt-1">{t.context}</p>}
                </figcaption>
              </figure>
            ))}
          </div>
        </Container>
      </Section>

      {/* ── Pricing ────────────────────────────────────────────────────── */}
      <Section id="pricing" show={d.pricing?.length > 0} className="border-t border-stone-200">
        <Container>
          <div className="text-center mb-16 md:mb-20">
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">Starting points</p>
            <SerifH2>Investment.</SerifH2>
            <p className="text-stone-600 mt-5 max-w-xl mx-auto">Indicative starting prices. Final scope is shared after a free site visit & detailed measurement.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {d.pricing.map((tier, idx) => (
              <div key={idx} data-testid={`pricing-${idx}`} className={`p-8 md:p-10 bg-white border ${tier.popular ? 'border-amber-700 ring-1 ring-amber-700/30' : 'border-stone-200'} relative flex flex-col`}>
                {tier.popular && <span className="absolute -top-3 left-8 bg-amber-700 text-white text-[10px] tracking-widest uppercase px-3 py-1">Most chosen</span>}
                <p className="text-xs uppercase tracking-widest text-stone-500">{tier.name}</p>
                <div className="my-5 flex items-baseline gap-2">
                  <span className="font-serif text-5xl md:text-6xl font-light text-amber-800" style={{ fontFamily: '"Playfair Display", serif' }}>{tier.price}</span>
                  {tier.unit && <span className="text-stone-500 text-xs">{tier.unit}</span>}
                </div>
                <ul className="space-y-3 mt-2 mb-8 text-sm text-stone-700 flex-1">
                  {tier.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/get-quote" data-testid={`pricing-cta-${idx}`} className="inline-flex items-center justify-center gap-2 border border-stone-900 hover:bg-stone-900 hover:text-white text-stone-900 text-sm font-medium py-3 transition-colors">
                  Get a quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* ── Footer CTA ─────────────────────────────────────────────────── */}
      <section className="border-t border-stone-200 bg-stone-900 text-stone-100 py-20 md:py-28">
        <Container className="text-center">
          <SerifH2 className="text-stone-100 mb-6">Let&apos;s build yours.</SerifH2>
          <p className="text-stone-400 max-w-xl mx-auto mb-10">A free site visit, transparent quote, and clear timelines. No obligations.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/get-quote" data-testid="footer-get-quote-btn" className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white text-base font-medium px-8 py-3.5 transition-colors">
              Get a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            {waLink && (
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-btn" className="inline-flex items-center gap-2 border border-stone-700 hover:border-stone-300 text-stone-100 text-base font-medium px-8 py-3.5 transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
          </div>
          <p className="text-stone-500 text-xs mt-12">© {new Date().getFullYear()} {d.brand?.name || 'Studio'}. All rights reserved.</p>
        </Container>
      </section>

      {/* ── Floating WhatsApp ──────────────────────────────────────────── */}
      {waLink && (
        <a
          href={waLink}
          target="_blank"
          rel="noreferrer"
          data-testid="floating-whatsapp"
          aria-label="Chat on WhatsApp"
          className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-50 inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-900/30 transition-transform hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
      )}

      {/* ── Lightbox ───────────────────────────────────────────────────── */}
      <Lightbox project={openProject} onClose={() => setOpenProject(null)} />
    </div>
  );
}
